/* useHeroQuote.js — Fase 2: conecta el modo "Lo hago yo" del hero al catálogo
 * RODAC real y al cálculo real del backend (POST /cotizador | /cotizador2).
 *
 * Envuelve el `h` de useHeroState (puerto fiel del handoff) SIN tocar el modo IA
 * (que sigue 100% mock hasta la Fase 4). Expone:
 *   h.catalog  → opciones reales {years, brands, models, versions, loadingField}
 *   h.real     → handlers de cascada para modo manual (onYear/onMarca/onModelo/onVersion)
 *   h.quote    → {monthly, loading, error, insurer} calculado por el backend
 *
 * El payload del backend es espejo de useCarCalculator.buildSubmitPayload()
 * (Hooks/useCarCalculator.js:397) con defaults documentados en rodacCatalog.js.
 * La primera cotización crea un Budget; los cambios siguientes re-cotizan el
 * mismo registro vía budget_id + secret (igual que /cotizar).
 *
 * Placa y seguro manual (backend/docs/plans/admin-recotizar-placa-seguro-manual.md):
 *   h.placas       → catálogo de placas (getStaticData.state); state.placa = id.
 *                    Su dirección va a RODAC y al cotizador (P3 · D-ADM-7).
 *   h.seguroManual → seguro capturado por un admin cuando RODAC no cotiza el
 *                    auto (P4 · D-ADM-8/17). Sin admin, nada de esto cambia.
 */
import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import axios from 'axios';
import { url, api, usarCotizadorV2 } from '../../../env';
import { AuthContext } from '../../../Context/AuthContext';
import { getDateInDays } from '../../Cotizador/functions/rodac';
import { validarSeguroManual, payloadSeguroManual, firmaSeguroManual } from '../../../Utils/seguroManual';
import {
  ajustarPagoInicial, limitesPagoInicial, ENGANCHE_MIN_PCT_DEFAULT, ENGANCHE_MAX_PCT_DEFAULT,
} from '../../../Utils/pagoInicial';
import {
  fetchYears, fetchBrandsUnion, fetchModelTypesUnion, fetchVersions,
  fetchInsuranceQuotes, pickInsurances, hasAnyInsurance, fetchVariants,
  addressForPlaca, insuranceCacheKey,
  PAYMENT_CONFIGS, DEFAULT_ADDRESS, CAR_TYPE_PICKUP,
  CAR_STATUS_NUEVO, CAR_STATUS_SEMINUEVO,
} from './rodacCatalog';
import { fetchAiSuggestion, fetchCarPrice } from './aiQuote';

const parseMoney = (v) => parseFloat(String(v ?? '').replace(/,/g, '')) || 0;
const fmtNote = (n) => '$' + Math.round(n).toLocaleString('es-MX');
const insurerName = (logo) => (logo || '').replace('Logo', '').split('.')[0].toUpperCase();
/* Seguro manual: las aseguradoras de RODAC vienen como logo ("LogoGnp.gif" → GNP);
 * "Otra…" es texto libre y se respeta entero (insurerName le cortaría el "S.A."). */
const manualInsurerName = (company) => (/^Logo/.test(company || '')
  ? insurerName(company)
  : String(company || '').toUpperCase());
const wait = (ms) => new Promise(r => setTimeout(r, ms));

/* fuzzy match contra catálogo RODAC (acentos/espacios/guiones fuera):
 * "RAV4" ≈ "RAV 4", "Mazda3" ≈ "MAZDA 3", "Quálitas" ≈ "QUALITAS" */
const squash = (s) => (s || '').toString().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');
const fuzzyFind = (list, key, target) => {
  const t = squash(target);
  if (!t) return null;
  return list.find(it => squash(it[key]) === t)
    || list.find(it => squash(it[key]).includes(t) || t.includes(squash(it[key])))
    || null;
};

/* Gama de una versión por el NOMBRE del trim (0=entrada, 1=media, 2=tope).
 * RODAC no da precio por versión ni las ordena por precio, así que clasificamos
 * por keywords comunes en México. Heurística: lo no reconocido cae a "media". */
function versionTier(desc) {
  const s = ' ' + (desc || '').toLowerCase() + ' ';
  const TOP = /exclusive|platinum|signature|limited|highline|luxury|titanium|denali|premium|advance plus|\bgt\b|\bgti\b|\bsr\b.*4x4/;
  const LOW = /\bsense\b|\bbase\b|austero|\bgl\b|trendline|\bdx\b|\bmt\b|i sport|\bsel\b\s*$/;
  if (TOP.test(s)) return 2;
  if (LOW.test(s)) return 0;
  return 1; // advance, sr, le, glx, comfortline, grand touring, etc.
}
/* Elige la versión acorde a version_pref ('baja'|'media'|'alta') por gama, NO por
 * índice de array (antes "media" caía en el tope por accidente de orden RODAC). */
function pickVersionByTrim(vs, pref) {
  if (!vs.length) return null;
  if (vs.length === 1) return vs[0];
  const scored = vs.map((v) => ({ v, t: versionTier(v.description) }));
  if (pref === 'alta') return scored.reduce((a, b) => (b.t >= a.t ? b : a)).v;
  if (pref === 'baja') return scored.reduce((a, b) => (b.t < a.t ? b : a)).v;
  // media: una de gama media si existe; si no, evita el tope (toma la más baja disponible)
  const mid = scored.filter((s) => s.t === 1);
  if (mid.length) return mid[0].v;
  return [...scored].sort((a, b) => a.t - b.t)[0].v;
}

/* noInsurance: false | 'auto' (ningún plazo cotizó seguro) | 'plazo' (el plazo
 * elegido no tiene opciones pero otros sí) → ResultStrip pinta el fallback
 * "Cotizar por WhatsApp" en vez del error crudo.
 * note: aviso corto bajo la renta (p.ej. ajuste por presupuesto de la IA).
 * manual/primaManual: la renta salió con el seguro manual del admin y su prima. */
const EMPTY_QUOTE = { monthly: 0, compareAt: null, loading: false, error: null, insurer: null, insurerLogo: null, insuranceId: null, noInsurance: false, note: null, manual: false, primaManual: null };

/* Seguro manual (P4): lo capturado por el admin (montos = prima total con IVA por
 * plazo) + si está aplicado. Los datos se quedan al cambiar de auto para prellenar. */
const SEGURO_MANUAL_VACIO = { company: '', montos: { 24: '', 36: '', 48: '', 60: '' }, folios: {}, foliosAuto: {}, aplicado: false };

/* Topes de valor residual por plazo (plan puro) — espejo de
 * backend/app/Helpers/ResidualHelper::LIMITS */
const RESIDUAL_LIMITS = { 24: 50, 36: 45, 48: 35, 60: 20 };
export const residualCapFor = (plazo) => RESIDUAL_LIMITS[plazo] ?? 0;

/* ── Ajuste por presupuesto CALIBRADO con la primera respuesta real ──────
 * El resumen del backend trae "Pago Mensual Base (incl IVA)" y "Importe del
 * Vehículo a Financiar": slope = base/financiado es el factor PMT exacto de
 * ese plazo → se despeja la tasa mensual r (bisección) y con ella se predice
 * el factor de cualquier plazo. servicios = total − base (≈ constante).
 * Así el ajuste cae al primer intento, sin más re-cotizaciones. */
const pmtFactor = (r, n) => r / (1 - Math.pow(1 + r, -n));
function solveRate(slope, n) {
  let lo = 1e-5, hi = 0.2;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (pmtFactor(mid, n) > slope) hi = mid; else lo = mid;
  }
  return (lo + hi) / 2;
}
/* minEng/maxEngLimit: límites del pago inicial en pesos (limitesPagoInicial).
 * El enganche sugerido redondea a miles, pero nunca queda debajo del mínimo ni
 * arriba del máximo. Sin límites (0/Infinity) se comporta como antes. */
export function planAdjustment({ target, monthly, precio, enganche, plazo, financedReal, baseReal, minEng = 0, maxEngLimit = Infinity }) {
  // HOLGURA, no exprimir: el ajuste solo sube a plazo cómodo (máx 48, salvo que
  // ya venga en 60) y enganche moderado (máx 25%). Nunca a 60 meses + 40%.
  const maxEng = Math.max(minEng, Math.min(maxEngLimit, Math.round(precio * 0.25 / 1000) * 1000));
  const maxPlazo = Math.max(48, plazo);
  const piso = Math.max(enganche, minEng);
  if (financedReal > 0 && baseReal > 0 && target > 0) {
    const servicios = Math.max(0, monthly - baseReal);
    const r = solveRate(baseReal / financedReal, plazo);
    for (const p of [36, 48, 60].filter(x => x >= plazo && x <= maxPlazo)) {
      const neededFinanced = Math.max(0, (target - servicios) / pmtFactor(r, p));
      let eng = Math.ceil((precio - neededFinanced) / 1000) * 1000;
      eng = Math.max(eng, piso);
      if (eng <= maxEng) return { plazo: p, enganche: eng };
    }
    return { plazo: Math.min(maxPlazo, 48), enganche: maxEng }; // cómodo, sin exprimir
  }
  // fallback burdo (si el resumen no trae esas llaves, p.ej. plan puro)
  const financed = precio - enganche;
  const neededFinanced = financed * (target / monthly) * (48 / plazo);
  let eng = Math.round((precio - neededFinanced) / 1000) * 1000;
  eng = Math.max(minEng, Math.min(maxEng, Math.max(eng, enganche)));
  return { plazo: Math.min(maxPlazo, 48), enganche: eng };
}

/* Límites de pago inicial (%) de getStaticData → downPayment.min/max. Si faltan o
 * no tienen sentido, el default 10/40. La landing nunca pasa de 40 % (como antes
 * del cambio), aunque enganche_maximo en la base sea mayor (prod: 80, dev: 60). */
export const LANDING_MAX_PCT = 40;
export function limitesDownPayment(dp) {
  const min = parseFloat(dp?.min);
  const max = parseFloat(dp?.max);
  const minPct = Number.isFinite(min) && min > 0 ? min : ENGANCHE_MIN_PCT_DEFAULT;
  const maxBase = Number.isFinite(max) && max > minPct ? max : Math.max(minPct, ENGANCHE_MAX_PCT_DEFAULT);
  return { minPct, maxPct: Math.max(minPct, Math.min(maxBase, LANDING_MAX_PCT)) };
}

/* Enganche sugerido (IA, auto alternativo): a miles, pero dentro de los límites. */
export function engancheSugerido(precio, pct, { minPct, maxPct }) {
  return ajustarPagoInicial({ monto: Math.round((precio * pct) / 1000) * 1000, base: precio, minPct, maxPct }).monto;
}

/* Mensaje del cotizador cuando falla: el `error` del backend y, si viene (422),
 * el mínimo/máximo sugerido. */
export function mensajeErrorCotizacion(data, fallback) {
  const base = (data && typeof data.error === 'string' && data.error) || fallback;
  const minS = +data?.min_sugerido;
  const maxS = +data?.max_sugerido;
  if (minS > 0) return `${base} Mínimo sugerido: ${fmtNote(Math.ceil(minS))}.`;
  if (maxS > 0) return `${base} Máximo sugerido: ${fmtNote(Math.floor(maxS))}.`;
  return base;
}

export function useHeroQuote(h, suggestRef = null) {
  const [years, setYears] = useState([]);       // [{id, year}]
  const [brands, setBrands] = useState([]);     // [{brand, carTypeIds}]
  const [models, setModels] = useState([]);     // [{type, carTypeId}]
  const [versions, setVersions] = useState([]); // [{id, description}]
  const [loadingField, setLoadingField] = useState(null);
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  // F4: true cuando la IA resolvió un auto REAL del catálogo RODAC → el modo
  // IA usa el mismo pipeline real que el manual (renta backend, modal real).
  const [aiResolved, setAiResolved] = useState(false);
  // Opciones "¿O prefieres…" agrupadas por modelo (cada una con sus variantes
  // Nueva/Usada que SÍ existen en RODAC), validadas server-side. Ver buildAiOptions.
  const [aiOptions, setAiOptions] = useState([]);
  // Catálogo de valores residuales (plan puro) — de /getStaticData
  const [residualAll, setResidualAll] = useState([]);
  // "Auto 1ª vez, luego manual": tras desbloquear, un cambio del usuario marca la
  // cotización como PENDIENTE (no recotiza solo) hasta que pulse "Cotizar ahora".
  const [staleQuote, setStaleQuote] = useState(false);
  // Placas (P3) — `state` de /getStaticData: [{id, state, default_zip, …}]
  const [placas, setPlacas] = useState([]);
  // Límites del pago inicial en % (getStaticData.downPayment); 10/40 mientras carga.
  // La ref es la copia síncrona para los callbacks (IA, alternativas, runQuote).
  const [downPayment, setDownPayment] = useState(() => limitesDownPayment(null));
  const downPaymentRef = useRef(downPayment);
  // Seguro manual (P4). La ref es la copia síncrona que lee runQuote: aplicar y
  // recotizar van en el mismo clic, sin esperar al render.
  const [seguroManual, setSeguroManualState] = useState(SEGURO_MANUAL_VACIO);
  const seguroManualRef = useRef(SEGURO_MANUAL_VACIO);

  // ids RODAC que el diseño no muestra pero el backend necesita
  const meta = useRef({ yearId: null, carTypeIds: [], carTypeId: null, descriptionId: null, carStatusId: CAR_STATUS_NUEVO });
  const yearsStatus = useRef(CAR_STATUS_NUEVO); // condición con la que se cargó `years`
  const staticData = useRef(null);              // /getStaticData: regime/residual defaults (puro)
  const staticPromise = useRef(null);           // /getStaticData en vuelo (no pedirlo 2 veces)
  const sinSeguroKeyRef = useRef(null);         // llave de seguros en la que RODAC no dio nada (D-ADM-17)
  const insuranceCache = useRef({ key: null, quotes: null });
  const insuranceChoice = useRef(null); // logo elegido en el paso Seguro del modal (F3)
  const budgetRef = useRef({ id: null, secret: null });
  const aiPromptRef = useRef(null); // prompt IA del usuario → se guarda en el Budget
  const debounceRef = useRef(null);
  const seqRef = useRef(0);
  const abortRef = useRef(null);        // cancela el POST /cotizador en vuelo al disparar otro
  const lastQuoteKeyRef = useRef(null); // dedupe: no repetir cotización idéntica a la última exitosa
  const aiBudgetRef = useRef(null);     // presupuesto mensual capturado por la IA (budget.monthly)
  const budgetAdjustRef = useRef(false);// ya intentamos UN ajuste automático por presupuesto
  const runQuoteRef = useRef(null);     // última runQuote (el effect la llama por ref, no se recrea por cotización)
  const quoteMonthlyRef = useRef(0);    // renta actual SIN meterla en deps del effect
  const manualRef = useRef(false);      // true tras desbloquear → no recotizar en cada cambio del usuario
  const bypassRef = useRef(false);      // acción explícita (alternativa) → recotiza aunque sea modo manual

  const { state, setField, mode } = h;

  // Admin Pei (type_user 1): el único que ve y manda el seguro manual (D-ADM-8),
  // mismo criterio de sesión que el MSI de HeroV3. El backend responde 403 a los demás.
  const { authInfo } = useContext(AuthContext) || {};
  const esAdmin = !!(authInfo?.isAuth && String(authInfo?.user?.type_user) === '1');

  const setSeguroManual = useCallback((update) => {
    const prev = seguroManualRef.current;
    const next = typeof update === 'function' ? update(prev) : update;
    if (next === prev) return;
    seguroManualRef.current = next;
    setSeguroManualState(next);
  }, []);

  /* /getStaticData (regime/residual defaults para plan puro + placas) — una sola
   * vez; las llamadas simultáneas comparten la petición y si falla se reintenta. */
  const ensureStaticData = useCallback(() => {
    if (staticData.current) return Promise.resolve(staticData.current);
    if (!staticPromise.current) {
      staticPromise.current = axios.get(`${api}/getStaticData`).then((res) => {
        const d = res.data;
        staticData.current = {
          regime: (d.regimes || []).find(r => r.id === 2) || null, // default como useCarCalculator:773
          residual: (d.residualValue || []).find(v => v.percentage === '5')
            || { id: 3, percentage: 5 },                            // fallback useCarCalculator:980
          downPayment: limitesDownPayment(d.downPayment),
        };
        downPaymentRef.current = staticData.current.downPayment;
        setDownPayment(staticData.current.downPayment);
        setResidualAll((d.residualValue || []).map(r => ({ id: r.id, percentage: +r.percentage })));
        setPlacas(Array.isArray(d.state) ? d.state : []);
        return staticData.current;
      }).catch(() => {
        staticPromise.current = null; // puro y placas quedan pendientes hasta que cargue
        return null;
      });
    }
    return staticPromise.current;
  }, []);

  /* Placas al montar, en los dos modos: el selector sale junto a Tipo·Motor
   * también en la IA (antes getStaticData solo se pedía en manual y en la IA real). */
  useEffect(() => { ensureStaticData(); }, [ensureStaticData]);

  /* Llave de seguros del render actual (auto + precio + placa + CP). La usan
   * insuranceOptions y el efecto de abajo. */
  const placaNow = +state.placa || DEFAULT_ADDRESS.license_state;
  const cacheKeyNow = insuranceCacheKey({
    descriptionId: meta.current.descriptionId,
    carStatusId: meta.current.carStatusId,
    precio: state.precio,
    placa: placaNow,
    zip: addressForPlaca(placas, placaNow).zip,
  });

  /* Otro auto, precio o placa (onYear/onMarca/onModelo/onVersion, pickVariant,
   * restartAI/switchMode, el PriceField o el selector Placas) cambian la llave:
   * el seguro manual deja de aplicar —sus datos se quedan para prellenar el
   * panel— y RODAC se vuelve a preguntar (D-ADM-17). Mover solo el plazo no. */
  useEffect(() => {
    if (sinSeguroKeyRef.current !== cacheKeyNow) sinSeguroKeyRef.current = null;
    setSeguroManual(sm => (sm.aplicado ? { ...sm, aplicado: false } : sm));
  }, [cacheKeyNow, setSeguroManual]);

  /* ── bootstrap: años + datos estáticos al entrar a modo manual ──
   * Orden cronológico como /cotizar: el usuario elige Año → Marca → Modelo. */
  useEffect(() => {
    if (mode !== 'manual' || (years.length && yearsStatus.current === CAR_STATUS_NUEVO)) return;
    let alive = true;
    setLoadingField('year');
    fetchYears(CAR_STATUS_NUEVO).then(ys => {
      if (!alive) return;
      yearsStatus.current = CAR_STATUS_NUEVO;
      meta.current.carStatusId = CAR_STATUS_NUEVO;
      setYears([...ys].sort((a, b) => +b.year - +a.year));
    }).catch(() => { /* dropdowns quedan vacíos; sin crash */ })
      .finally(() => alive && setLoadingField(null));

    ensureStaticData();
    return () => { alive = false; };
  }, [mode, years.length, setField, ensureStaticData]);

  /* ── cascada real (solo modo manual) ── */
  const resetChain = useCallback((patch) => {
    meta.current.carTypeIds = []; meta.current.carTypeId = null; meta.current.descriptionId = null;
    setModels([]); setVersions([]);
    setField({ modelo: '', version: '', precio: 0, carroceria: '', motor: '', enganche: 0, ...patch });
    setQuote(EMPTY_QUOTE);
  }, [setField]);

  const onYear = useCallback((label) => {
    const y = years.find(it => it.year === label);
    if (!y) return;
    meta.current.yearId = y.id;
    resetChain({ year: label, marca: '' });
    setBrands([]); setLoadingField('marca');
    fetchBrandsUnion(y.id).then(setBrands).catch(() => { }).finally(() => setLoadingField(null));
  }, [years, resetChain]);

  const onMarca = useCallback((brand) => {
    const b = brands.find(it => it.brand === brand);
    if (!b) return;
    meta.current.carTypeIds = b.carTypeIds;
    resetChain({ marca: brand });
    setLoadingField('modelo');
    fetchModelTypesUnion(meta.current.yearId, brand, b.carTypeIds)
      .then(setModels).catch(() => { }).finally(() => setLoadingField(null));
  }, [brands, resetChain]);

  const onModelo = useCallback((type) => {
    const m = models.find(it => it.type === type);
    if (!m) return;
    meta.current.carTypeId = m.carTypeId;
    meta.current.descriptionId = null;
    setVersions([]);
    // pickup real → fijamos la carrocería del diseño automáticamente
    setField({
      modelo: type, version: '', precio: 0, enganche: 0,
      carroceria: m.carTypeId === CAR_TYPE_PICKUP ? 'pickup' : '',
      motor: '',
    });
    setQuote(EMPTY_QUOTE);
    setLoadingField('version');
    fetchVersions(m.carTypeId, meta.current.yearId, state.marca, type)
      .then(setVersions).catch(() => { }).finally(() => setLoadingField(null));
  }, [models, setField, state.marca]);

  const onVersion = useCallback((description) => {
    const v = versions.find(it => it.description === description);
    if (!v) return;
    meta.current.descriptionId = v.id;
    // El precio NO viene de RODAC: lo captura el usuario (igual que /cotizar).
    setField({ version: description, enganche: 0 });
    insuranceCache.current = { key: null, quotes: null };
    insuranceChoice.current = null; // otro auto → la elección de aseguradora ya no aplica
    setQuote(EMPTY_QUOTE);
  }, [versions, setField]);

  /* ── cotización real contra el backend ── */
  const runQuote = useCallback(async () => {
    const { precio, motor, plan, plazo, enganche } = state;
    const { descriptionId, carTypeId, yearId, carStatusId } = meta.current;
    const isPuro = plan === 'puro';
    const complete = descriptionId && carTypeId && precio > 0 && motor && plan && plazo > 0
      && (!isPuro || staticData.current); // puro necesita regime/residual de getStaticData
    if (!complete) return;

    // Sin pago inicial todavía (precio recién puesto): arranca en el mínimo, igual
    // que el formulario de /cotizar. Red de seguridad: nunca se cotiza un pago
    // inicial fuera de rango; se ajusta al límite y el effect vuelve a cotizar.
    const engOk = ajustarPagoInicial({ monto: enganche, base: precio, ...downPaymentRef.current });
    if (engOk.monto !== +enganche) { setField({ enganche: engOk.monto }); return; }

    // residual del plan puro: el elegido por el usuario, con tope por plazo
    // (espejo de ResidualHelper::capResidualValueId — el backend re-capea igual)
    const cap = residualCapFor(plazo);
    let residual = isPuro ? (state.residual || staticData.current?.residual || { id: 3, percentage: 5 }) : null;
    if (residual && cap > 0 && +residual.percentage > cap) {
      residual = [...residualAll].filter(r => r.percentage <= cap).sort((a, b) => b.percentage - a.percentage)[0] || residual;
    }

    // placa → dirección con la que cotizan RODAC y el backend (P3 · D-ADM-7)
    const placa = +state.placa || DEFAULT_ADDRESS.license_state;
    const address = addressForPlaca(placas, placa);
    const cacheKey = insuranceCacheKey({ descriptionId, carStatusId, precio, placa, zip: address.zip });

    // Seguro manual del admin (P4): solo si RODAC ya dijo "nada" para esta misma
    // llave (D-ADM-17) y lo capturado cubre los plazos que guarda el motor. Con
    // manual no se pregunta a RODAC (quotes = {}), así que nunca hay `picked`.
    const sm = seguroManualRef.current;
    const rodacSinSeguro = esAdmin && sinSeguroKeyRef.current === cacheKey;
    const datosManual = { plazo, company: sm.company, montos: sm.montos, folios: sm.folios, foliosAuto: sm.foliosAuto };
    const manual = rodacSinSeguro && sm.aplicado && validarSeguroManual(datosManual).ok
      ? payloadSeguroManual(datosManual)
      : null;

    // dedupe: si nada relevante cambió desde la última cotización exitosa, no repetir
    const quoteKey = JSON.stringify([descriptionId, carStatusId, +precio, motor, plan, +plazo, +enganche,
      isPuro ? residual?.id : null, insuranceChoice.current, placa, address.zip,
      manual ? firmaSeguroManual(sm) : null]);
    if (quoteKey === lastQuoteKeyRef.current && quote.monthly > 0) return;

    // cancela el POST /cotizador anterior si sigue en vuelo
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    const seq = ++seqRef.current;
    setQuote(q => ({ ...q, loading: true, error: null, noInsurance: false }));
    try {
      // 1) seguros RODAC (cache por vehículo+precio+placa; el plazo solo re-selecciona)
      let quotes = insuranceCache.current.key === cacheKey ? insuranceCache.current.quotes : null;
      // D-ADM-17: el admin ya vio que RODAC no da nada para esta llave → no volver
      // a preguntar (2 llamadas de ~3 s + 1.5 s de reintento) cada vez que mueve el plazo.
      if (!quotes && rodacSinSeguro) quotes = {};
      if (!quotes) {
        quotes = await fetchInsuranceQuotes({ descriptionId, carTypeId, amount: precio, carStatusId, address });
        if (seq !== seqRef.current) return;
        // Hay autos donde RODAC responde 200 pero TODAS las aseguradoras vienen
        // con HasError:true (a veces por un 502 transitorio del upstream, p.ej.
        // Chubb/ABA) → 1 retry automático antes de rendirnos.
        if (!hasAnyInsurance(quotes)) {
          await wait(1500);
          if (seq !== seqRef.current) return;
          quotes = await fetchInsuranceQuotes({ descriptionId, carTypeId, amount: precio, carStatusId, address });
          if (seq !== seqRef.current) return;
        }
        // nada en ningún plazo ('auto') → se recuerda la llave para el panel del admin
        sinSeguroKeyRef.current = hasAnyInsurance(quotes) ? null : cacheKey;
      }
      const picked = manual ? null : pickInsurances(quotes, plazo, insuranceChoice.current);
      if (!picked && !manual) {
        // NO cachear resultados sin opción para el plazo elegido: "Reintentar"
        // (y cambiar de plazo) debe volver a llamar a RODAC, no releer vacíos.
        insuranceCache.current = { key: null, quotes: null };
        setQuote({
          ...EMPTY_QUOTE,
          noInsurance: hasAnyInsurance(quotes) ? 'plazo' : 'auto',
        });
        return;
      }
      if (picked) {
        insuranceCache.current = { key: cacheKey, quotes };
        // RODAC sí cotizó esta llave: gana RODAC y el manual se des-aplica (D-ADM-17)
        if (seguroManualRef.current.aplicado) setSeguroManual(s => ({ ...s, aplicado: false }));
      }

      // 2) payload espejo de buildSubmitPayload (useCarCalculator.js:397)
      const insurances = {};
      if (picked) {
        Object.entries(picked.byPlazo).forEach(([p, opt]) => {
          if (opt) insurances[p] = { insurance: opt.amount, insurance_id: opt.id, company: opt.logo };
        });
      }
      const payload = {
        // budgets.name es NOT NULL; el hero no pide nombre hasta el modal de
        // solicitud (F3), donde se actualiza con el real.
        name: 'Visitante landing',
        ai_prompt: aiPromptRef.current || undefined,
        car_type: carTypeId === CAR_TYPE_PICKUP ? 'PICKUP' : 'AUTO',
        car_status: carStatusId === CAR_STATUS_SEMINUEVO ? 'SEMINUEVO' : 'NUEVO',
        year: yearId,
        brand: state.marca,
        type: state.modelo,
        car_id: descriptionId,
        car: state.version,
        price: +precio,
        accesories: 0,
        term: +plazo,
        terms: PAYMENT_CONFIGS,
        down_payment: +enganche,
        license_state: placa,
        insurance_id: picked ? picked.selected.id : null,
        insurance: picked ? picked.selected.amount : null,
        date: getDateInDays(10),
        insurances,
        zip: address.zip,
        city: address.ciudad,
        user_state: address.estado,
        colony: address.colonia,
        delegation: address.municipio,
        typeCoverage: isPuro
          ? { id: 2, type_coverage: 'PURO' }
          : { id: 1, type_coverage: 'FINANCIERO' },
        // cotizador lee typeEngineName.id; cotizador2 (puro) lee typeEngineName.type_engine
        // (string) — misma convención que useCarCalculator:741
        typeEngine: {
          typeEngineName: motor === 'híbrido'
            ? { id: 2, type_engine: 'Híbrido/Eléctrico' }
            : { id: 1, type_engine: 'Gasolina/Diesel' },
          id: motor === 'híbrido' ? 2 : 1,
        },
        budget_id: budgetRef.current.id,
        secret: budgetRef.current.secret,
        ...(isPuro ? {
          typeRegime: staticData.current.regime || { id: 2 },
          residualValue: residual,
          amountVehicle: +precio,
          expectedKm: null,
          plazoVal: +plazo,
        } : {}),
        // seguro del admin: reemplaza insurance, insurance_id (null: el id lo pone
        // el backend) e insurances, y agrega seguro_manual (contrato §4.1)
        ...(manual || {}),
      };

      // 3) mismo endpoint y headers que useCarCalculator.submit()
      // const endpoint = (isPuro ? 'cotizador2' : 'cotizador') + (usarCotizadorV2 ? '-v2' : '');
      const endpoint = (isPuro ? 'cotizador2' : 'cotizador') + '-v2';
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await axios.post(`${url}/${endpoint}`, payload, { headers, signal });
      if (seq !== seqRef.current) return;
      if (res.data.error) throw new Error(res.data.error);

      budgetRef.current = { id: res.data.budget_id, secret: res.data.budget_secret };
      const monthly = parseMoney(res.data.resumen?.['Pago Mensual Total (incl IVA)']);
      if (!monthly) throw new Error('No pudimos calcular tu renta. Intenta de nuevo.');

      // Precio "tachado" (oferta REAL, no inventada): renta aproximada con la
      // SIGUIENTE aseguradora más barata del plazo elegido. La diferencia de
      // prima se reparte en el plazo. Solo se muestra si hay 2+ aseguradoras,
      // el ahorro mensual es perceptible (>= $100) y la elegida ES la más
      // barata (si el usuario escogió otra en el modal, no hay "oferta").
      let compareAt = null;
      const opts = (picked && quotes[plazo]) || [];
      if (opts.length > 1 && picked.selected.id === picked.cheapest.id) {
        const sorted = [...opts].sort((a, b) => a.amount - b.amount);
        const deltaMonthly = (sorted[1].amount - sorted[0].amount) / plazo;
        if (deltaMonthly >= 100) compareAt = Math.round(monthly + deltaMonthly);
      }

      lastQuoteKeyRef.current = quoteKey;

      /* ── Fallback de presupuesto (IA) ─────────────────────────────────
       * El LLM eligió el auto con una fórmula simplificada; la renta REAL
       * trae seguro/comisión/gestoría y puede pasarse del presupuesto que
       * dio el usuario. UN solo ajuste automático (como lo haría a mano):
       * plazo 60 + el enganche estimado para caber, tope 40%. */
      const target = aiBudgetRef.current;
      let note = null;
      if (target && monthly > target * 1.03) {
        if (!budgetAdjustRef.current) {
          budgetAdjustRef.current = true;
          const lim = limitesPagoInicial({ base: precio, ...downPaymentRef.current });
          const adj = planAdjustment({
            target, monthly, precio, enganche, plazo,
            financedReal: parseMoney(res.data.resumen?.['Importe del Vehículo a Financiar']),
            baseReal: parseMoney(res.data.resumen?.['Pago Mensual Base (incl IVA)']),
            minEng: lim.min, maxEngLimit: lim.max,
          });
          // solo si el ajuste realmente cambia algo (si ya estaba en 60 meses
          // con enganche al tope, no hay nada que reintentar)
          if (adj && (adj.plazo !== plazo || adj.enganche > enganche)) {
            setQuote(q => ({ ...q, loading: true })); // seguimos "calculando"
            setField({ plazo: adj.plazo, enganche: adj.enganche }); // re-cotiza
            return;
          }
        }
        // ya ajustamos (o no había margen) y sigue arriba — avisar honestamente
        note = `Quedó ${fmtNote(monthly)} con el máximo ajuste; tu presupuesto era ${fmtNote(target)}/mes.`;
      } else if (target && budgetAdjustRef.current) {
        note = `Ajustamos plazo y pago inicial para caber en tu presupuesto de ${fmtNote(target)}/mes.`;
      }

      if (manual) {
        // Seguro manual: el id lo genera el backend (D-ADM-10) y regresa en
        // seguro_seleccionado (D-ADM-15); es el que pide /updateBudget al enviar.
        const company = manual.insurances[plazo].company;
        setQuote({
          ...EMPTY_QUOTE, monthly, note,
          insurer: manualInsurerName(company),
          insurerLogo: company,
          insuranceId: res.data.seguro_seleccionado?.internal_id ?? null,
          manual: true,
          primaManual: manual.insurance,
        });
        return;
      }
      setQuote({
        ...EMPTY_QUOTE, monthly, compareAt, note,
        insurer: insurerName(picked.selected.logo),
        insurerLogo: picked.selected.logo,
        insuranceId: picked.selected.id,
      });
    } catch (err) {
      // petición cancelada por una más nueva → silencio (la nueva pinta su resultado)
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      if (seq !== seqRef.current) return;
      const data = err?.response?.data;
      // el 403/422 del seguro manual trae un `message` legible (§4.1)
      // el cotizador manda su `error` (422 con min_sugerido/max_sugerido si el
      // pago inicial no alcanza o se pasa) → se muestra tal cual, con la sugerencia
      const msg = (manual && data?.message)
        || mensajeErrorCotizacion(data, err.message || 'No pudimos calcular tu renta.');
      setQuote(q => ({ ...q, loading: false, error: msg, noInsurance: false }));
    }
  }, [state, quote.monthly, residualAll, setField, placas, esAdmin, setSeguroManual]);

  // Refs frescas para que el effect de cotización NO se recree (ni se dispare) en
  // cada cotización — solo cuando cambian los inputs (state).
  runQuoteRef.current = runQuote;
  quoteMonthlyRef.current = quote.monthly;

  /* Dispara la cotización cuando la calculadora está completa.
   * "Auto la 1ª vez, luego manual": antes de desbloquear (manualRef=false) cotiza
   * solo (debounced) — incluido el reveal de la IA. Tras desbloquear, un cambio del
   * usuario NO recotiza: marca PENDIENTE y se cotiza al pulsar "Cotizar ahora"
   * (requote). Las alternativas usan bypassRef para recotizar al instante. */
  useEffect(() => {
    if (mode !== 'manual' && !aiResolved) return;
    const { precio, motor, plan, plazo, enganche } = state;
    // Precio recién puesto y sin pago inicial: arranca en el mínimo (como /cotizar).
    if (meta.current.descriptionId && precio > 0 && !(enganche > 0)) {
      setField({ enganche: limitesPagoInicial({ base: precio, ...downPaymentRef.current }).min });
      return;
    }
    const complete = meta.current.descriptionId && precio > 0 && motor && plan && plazo > 0 && enganche > 0;
    if (!complete) {
      setStaleQuote(false);
      setQuote(q => (q.monthly || q.error || q.noInsurance ? EMPTY_QUOTE : q));
      return;
    }
    if (manualRef.current && quoteMonthlyRef.current > 0 && !bypassRef.current) {
      setStaleQuote(true); // ya desbloqueado y cambió algo → espera "Cotizar ahora"
      return;
    }
    bypassRef.current = false;
    setStaleQuote(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { if (runQuoteRef.current) runQuoteRef.current(); }, 700);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, aiResolved, state]);

  /* "Cotizar ahora": recotiza manualmente lo pendiente (tras editar ya desbloqueado). */
  const requote = useCallback(() => {
    setStaleQuote(false);
    clearTimeout(debounceRef.current);
    if (runQuoteRef.current) runQuoteRef.current();
  }, []);

  /* HeroV3 activa el modo manual al desbloquear (lead capturado): de ahí en
   * adelante los cambios del usuario esperan "Cotizar ahora". */
  const setManualQuote = useCallback((on) => { manualRef.current = !!on; }, []);

  /* limpiar cotización al cambiar de modo (el estado ya lo resetea switchMode) */
  const switchMode = useCallback((m) => {
    seqRef.current++;
    insuranceChoice.current = null;
    aiBudgetRef.current = null;
    aiPromptRef.current = null;
    budgetAdjustRef.current = false;
    lastQuoteKeyRef.current = null;
    setAiResolved(false);
    setAiOptions([]);
    setQuote(EMPTY_QUOTE);
    setStaleQuote(false);
    manualRef.current = false;
    bypassRef.current = false;
    h.switchMode(m);
  }, [h]);

  /* "Cambiar" en el PromptStrip → además de resetear el estado del handoff,
   * limpia la resolución real para que la siguiente corrida parta de cero */
  const restartAI = useCallback(() => {
    seqRef.current++;
    insuranceChoice.current = null;
    insuranceCache.current = { key: null, quotes: null };
    aiBudgetRef.current = null;
    aiPromptRef.current = null;
    budgetAdjustRef.current = false;
    lastQuoteKeyRef.current = null;
    setAiResolved(false);
    setAiOptions([]);
    setQuote(EMPTY_QUOTE);
    setStaleQuote(false);
    manualRef.current = false;
    bypassRef.current = false;
    h.restartAI();
  }, [h]);

  /* ── F4: suggester REAL para el modo IA ──────────────────────────────────
   * 1) intención estructurada del backend (LLM con grounding de marcas RODAC)
   * 2) resolución fuzzy contra el catálogo real (marca→modelo→versión)
   * 3) deja meta/catálogo listos para que el pipeline real cotice la renta.
   * Si CUALQUIER paso falla, lanza → useAI cae al mock del handoff. */
  const aiSuggestReal = useCallback(async (prompt) => {
    aiPromptRef.current = prompt; // persiste para el primer Budget que se cree
    const staticP = ensureStaticData(); // en paralelo; se espera al final

    const ai = await fetchAiSuggestion(prompt, []); // 1ª pasada sin marcas si aún no hay

    // condición detectada por la IA (nuevo/seminuevo) → años de ESA condición
    let statusId = ai.condicion === 'seminuevo' ? CAR_STATUS_SEMINUEVO : CAR_STATUS_NUEVO;
    let ys = (years.length && yearsStatus.current === statusId) ? years : null;
    if (!ys) {
      ys = [...await fetchYears(statusId)].sort((a, b) => +b.year - +a.year);
      yearsStatus.current = statusId;
      setYears(ys);
    }
    // año pedido por la IA; si no existe en el catálogo, el MÁS CERCANO (no el más nuevo)
    const reqYearNum = parseInt(ai.year, 10);
    let yearEntry = ys.find(y => y.year === String(ai.year))
      || (reqYearNum ? [...ys].sort((a, b) => Math.abs(+a.year - reqYearNum) - Math.abs(+b.year - reqYearNum))[0] : null)
      || ys[0];

    let bs = brands;
    if (!bs.length || meta.current.yearId !== yearEntry.id) {
      bs = await fetchBrandsUnion(yearEntry.id);
      setBrands(bs);
    }

    // marca: match fuzzy; si el LLM propuso una que no existe en RODAC,
    // re-preguntamos UNA vez con la lista real como grounding
    let brandEntry = fuzzyFind(bs, 'brand', ai.marca);
    let suggestion = ai;
    if (!brandEntry) {
      suggestion = await fetchAiSuggestion(prompt, bs.map(b => b.brand));
      brandEntry = fuzzyFind(bs, 'brand', suggestion.marca);
      if (!brandEntry) throw new Error('Marca fuera de catálogo');
    }

    // ── Resolver el MODELO en su AÑO REAL ────────────────────────────────────
    // El catálogo es POR AÑO (año → marcas → modelos) y un modelo puede existir en
    // unos años y NO en otros: el SPARK clásico (barato) está en 2017-2021 pero NO
    // en 2023; en 2026 "SPARK" es el EUV ELÉCTRICO (otro auto, mucho más caro). Si
    // el modelo no está en el año pedido, lo buscamos en los años MÁS CERCANOS al
    // pedido (NUNCA saltando al más nuevo) — primero misma condición, luego la otra —
    // para caer en el MISMO auto/segmento que el usuario quería, no en una variante cara.
    let ms = await fetchModelTypesUnion(yearEntry.id, brandEntry.brand, brandEntry.carTypeIds);
    let modelEntry = fuzzyFind(ms, 'type', suggestion.modelo);

    if (!modelEntry) {
      const reqYear = +yearEntry.year; // año-objetivo (el que pidió la IA, o el más cercano)
      // cercanía al año pedido; empate → el más VIEJO (más barato)
      const byProximity = (a, b) =>
        Math.abs(+a.year - reqYear) - Math.abs(+b.year - reqYear) || +a.year - +b.year;
      const otherStatus = statusId === CAR_STATUS_SEMINUEVO ? CAR_STATUS_NUEVO : CAR_STATUS_SEMINUEVO;
      const otherYs = [...await fetchYears(otherStatus)].sort((a, b) => +b.year - +a.year);
      const attempts = [
        // misma condición, años MÁS CERCANOS al pedido (así "Spark 2023" cae en el
        // Spark clásico 2021, no en el EUV eléctrico 2026):
        ...ys.filter((y) => y.id !== yearEntry.id).sort(byProximity).slice(0, 8)
          .map((y) => ({ st: statusId, y, list: ys })),
        // luego la otra condición, también por cercanía al año pedido:
        ...[...otherYs].sort(byProximity).slice(0, 6)
          .map((y) => ({ st: otherStatus, y, list: otherYs })),
      ];
      for (const a of attempts) {
        const bs2 = await fetchBrandsUnion(a.y.id);
        const be2 = fuzzyFind(bs2, 'brand', brandEntry.brand);
        if (!be2) continue;
        const ms2 = await fetchModelTypesUnion(a.y.id, be2.brand, be2.carTypeIds);
        const hit = fuzzyFind(ms2, 'type', suggestion.modelo);
        if (hit) {
          statusId = a.st; yearEntry = a.y; brandEntry = be2; ms = ms2; modelEntry = hit;
          ys = a.list; yearsStatus.current = a.st; setYears(a.list);
          break;
        }
      }
    }

    if (!ms.length) throw new Error('Sin modelos');
    setModels(ms);
    // Último recurso (el modelo no existe en NINGÚN año/condición): auto de
    // pasajeros real del año propuesto, nunca ms[0] alfabético (suele ser comercial).
    if (!modelEntry) modelEntry = ms.find((m) => m.carTypeId !== CAR_TYPE_PICKUP) || ms[0];

    const vs = await fetchVersions(modelEntry.carTypeId, yearEntry.id, brandEntry.brand, modelEntry.type);
    if (!vs.length) throw new Error('Sin versiones');
    setVersions(vs);
    // RODAC NO ordena las versiones por precio (p.ej. Kicks llega
    // [Advance, EXCLUSIVE, Platinum, Sense]), así que elegir por índice fijo hacía
    // que "media" cayera en una versión TOPE por accidente de orden. Rankeamos por
    // nombre de trim para que baja/media = económicas y alta = tope.
    const ver = pickVersionByTrim(vs, suggestion.version_pref);

    // deja todo listo para el pipeline real (renta backend + modal 4 pasos)
    meta.current = {
      yearId: yearEntry.id,
      carTypeIds: brandEntry.carTypeIds,
      carTypeId: modelEntry.carTypeId,
      descriptionId: ver.id,
      carStatusId: statusId,
    };
    // condición visible para el resumen/WhatsApp (no es parte de la animación)
    setField({ condicion: statusId === CAR_STATUS_SEMINUEVO ? 'seminuevo' : 'nuevo' });
    // "¿O prefieres…" — se arma en 2º plano (no bloquea la animación),
    // validando contra RODAC qué variantes Nueva/Seminueva existen de verdad.
    buildAiOptions(
      { marca: brandEntry.brand, modelo: modelEntry.type },
      suggestion.alternativas || []
    );
    insuranceCache.current = { key: null, quotes: null };
    insuranceChoice.current = null;
    // presupuesto mensual del usuario (si lo dio) → activa el fallback de
    // ajuste automático cuando la renta REAL se pase del límite
    aiBudgetRef.current = suggestion.budget?.monthly || null;
    budgetAdjustRef.current = false;
    lastQuoteKeyRef.current = null;

    // Precio REAL de mercado del auto YA resuelto (marca/modelo/AÑO real, que puede
    // diferir del que propuso el LLM), vía OpenAI web search cacheado en backend; en
    // paralelo con getStaticData. Si tarda/falla, cae al precio_estimado del LLM. La
    // animación de "Aria construyendo…" cubre la espera; tras el 1er hit queda cacheado.
    const estPrice = Math.max(0, Math.round(+suggestion.precio_estimado || 0));
    const [, realPrice] = await Promise.all([
      staticP,
      fetchCarPrice({
        marca: brandEntry.brand,
        modelo: modelEntry.type,
        year: yearEntry.year,
        condicion: statusId === CAR_STATUS_SEMINUEVO ? 'seminuevo' : 'nuevo',
      }),
    ]);
    setAiResolved(true);

    // El web search refina el precio, pero es INESTABLE y sesgado a versiones caras
    // (devolvía $499-529k para el Kicks cuando la versión media ronda $370-420k),
    // disparando la renta sobre el presupuesto. Lo usamos solo si no se dispara
    // más de 15% sobre el estimado del LLM (que es estable y moderado); si excede,
    // tope = estimado×1.15. Por abajo sí lo dejamos (un real más barato beneficia).
    const precio = (realPrice > 0 && estPrice > 0)
      ? Math.min(realPrice, Math.round(estPrice * 1.15))
      : (realPrice > 0 ? realPrice : estPrice);
    // % de la IA dentro de los límites; a miles, pero nunca fuera de min/max
    const lim = downPaymentRef.current;
    const engPct = Math.max(lim.minPct / 100, Math.min(lim.maxPct / 100, +suggestion.engPct || 0.20));
    return {
      marca: brandEntry.brand,
      modelo: modelEntry.type,
      year: yearEntry.year,
      version: ver.description,
      precio,
      carroceria: modelEntry.carTypeId === CAR_TYPE_PICKUP ? 'pickup' : (suggestion.carroceria || 'sedan'),
      motor: suggestion.motor === 'híbrido' ? 'híbrido' : 'gasolina',
      plan: suggestion.plan === 'puro' ? 'puro' : 'financiero',
      plazo: [24, 36, 48, 60].includes(+suggestion.plazo) ? +suggestion.plazo : 48,
      enganche: engancheSugerido(precio, engPct, lim),
      engPct,
      budget: suggestion.budget || { monthly: null, total: null },
      narrative: suggestion.razon || null, // el stream usa la razón del LLM
    };
  }, [years, brands, ensureStaticData, setField]);

  /* Arma la sección "¿O prefieres…": filas de auto con miniatura y toggle
   * Nueva/Seminueva. El backend valida cada modelo contra RODAC (nunca un
   * seminuevo inexistente). Corre en 2º plano (no bloquea la animación).
   * main = {marca, modelo}. */
  const buildAiOptions = useCallback(async (main, alternativas) => {
    setAiOptions([]);
    const recKey = squash(main.marca) + '|' + squash(main.modelo);
    // modelos candidatos: el principal + las alternativas del LLM (dedup)
    const seen = new Set();
    const candidates = [];
    [main, ...(alternativas || [])].forEach((m) => {
      const k = squash(m.marca) + '|' + squash(m.modelo);
      if (m.marca && m.modelo && !seen.has(k)) { seen.add(k); candidates.push({ marca: m.marca, modelo: m.modelo }); }
    });
    const variants = await fetchVariants(candidates, 2);
    const subOf = (ref) => (ref && ref.carTypeId === CAR_TYPE_PICKUP ? 'Pickup' : 'Auto');
    const rows = variants.map((v) => {
      const nueva = v.nueva ? { condicion: 'nuevo', label: 'Nueva', ...v.nueva } : null;
      const usada = v.usada ? { condicion: 'seminuevo', label: `Seminueva '${String(v.usada.year).slice(-2)}`, ...v.usada } : null;
      if (!nueva && !usada) return null;
      const ref = nueva || usada;
      return {
        key: squash(v.marca) + '|' + squash(v.modelo),
        marca: ref.brand || v.marca, modelo: ref.type || v.modelo,
        sub: subOf(ref), isRec: (squash(v.marca) + '|' + squash(v.modelo)) === recKey,
        nueva, usada,
      };
    }).filter(Boolean);
    rows.sort((a, b) => (b.isRec ? 1 : 0) - (a.isRec ? 1 : 0)); // el recomendado primero
    setAiOptions(rows);
  }, []);

  /* Variante elegida en un chip → ya viene resuelta del backend (yearId, brand,
   * type, carTypeId, condicion). Solo falta versión + precio real + re-cotizar. */
  const pickVariant = useCallback(async (opt) => {
    if (!opt || quote.loading) return;
    // ya está activa esta combinación → no hacer nada
    if (squash(state.marca) === squash(opt.brand) && squash(state.modelo) === squash(opt.type)
      && String(state.year) === String(opt.year)
      && (state.condicion === opt.condicion)) return;
    try {
      setQuote(q => ({ ...q, loading: true, error: null, noInsurance: false }));
      const statusId = opt.condicion === 'seminuevo' ? CAR_STATUS_SEMINUEVO : CAR_STATUS_NUEVO;
      // sincroniza el catálogo de años con la condición elegida (dropdown Año)
      if (yearsStatus.current !== statusId) {
        const ys = [...await fetchYears(statusId)].sort((a, b) => +b.year - +a.year);
        yearsStatus.current = statusId; setYears(ys);
      }
      // recarga marcas/modelos del nuevo año+marca para que los dropdowns
      // contengan la opción elegida (si no, Modelo se ve vacío: la lista seguía
      // siendo la del auto anterior y "COROLLA" no estaba ahí).
      const bs = await fetchBrandsUnion(opt.yearId);
      setBrands(bs);
      const brandEntry = fuzzyFind(bs, 'brand', opt.brand);
      const ms = await fetchModelTypesUnion(opt.yearId, opt.brand, brandEntry?.carTypeIds || [opt.carTypeId]);
      setModels(ms);
      const vs = await fetchVersions(opt.carTypeId, opt.yearId, opt.brand, opt.type);
      if (!vs.length) throw new Error('sin versiones');
      setVersions(vs);
      const ver = pickVersionByTrim(vs, 'media'); // versión media por gama, no índice fijo

      meta.current = {
        yearId: opt.yearId, carTypeIds: brandEntry?.carTypeIds || [opt.carTypeId], carTypeId: opt.carTypeId,
        descriptionId: ver.id, carStatusId: statusId,
      };
      insuranceCache.current = { key: null, quotes: null };
      insuranceChoice.current = null;
      budgetAdjustRef.current = false;
      lastQuoteKeyRef.current = null;
      bypassRef.current = true;
      setStaleQuote(false);

      // precio real (web search cacheado) del auto exacto; fallback al actual
      const realPrice = await fetchCarPrice({ marca: opt.brand, modelo: opt.type, year: opt.year, condicion: opt.condicion });
      const precio = realPrice > 0 ? realPrice : state.precio;
      const engPct = state.precio > 0 && state.enganche > 0 ? state.enganche / state.precio : 0.2;
      setField({
        marca: opt.brand, modelo: opt.type, version: ver.description, year: String(opt.year),
        condicion: statusId === CAR_STATUS_SEMINUEVO ? 'seminuevo' : 'nuevo',
        precio, enganche: engancheSugerido(precio, engPct, downPaymentRef.current),
        carroceria: opt.carTypeId === CAR_TYPE_PICKUP ? 'pickup' : (state.carroceria || 'sedan'),
      });
      // la re-cotización la dispara el effect al cambiar el estado
    } catch (e) {
      setQuote(q => ({ ...q, loading: false }));
    }
  }, [state, quote.loading, setField]);

  // el hook useAI del handoff lee el suggester desde esta ref (ver heroCore.useAI)
  if (suggestRef) suggestRef.current = aiSuggestReal;

  /* ── Valor residual (plan puro) ──────────────────────────────────────────
   * Opciones del catálogo con tope por plazo (24→50%, 36→45%, 48→35%, 60→20%,
   * espejo de ResidualHelper). Si el usuario baja el plazo y su residual ya no
   * cabe, se ajusta solo al máximo permitido (igual que hace el backend). */
  const residualOptions = state.plazo
    ? residualAll.filter(r => r.percentage <= residualCapFor(state.plazo))
    : residualAll;

  const setResidual = useCallback((percentage) => {
    const row = residualAll.find(r => r.percentage === +percentage);
    if (row) setField({ residual: row });
  }, [residualAll, setField]);

  useEffect(() => {
    if (state.plan !== 'puro' || !state.residual || !state.plazo || !residualAll.length) return;
    const cap = residualCapFor(state.plazo);
    if (cap > 0 && +state.residual.percentage > cap) {
      const clamped = [...residualAll].filter(r => r.percentage <= cap).sort((a, b) => b.percentage - a.percentage)[0];
      if (clamped) setField({ residual: clamped });
    }
  }, [state.plan, state.plazo, state.residual, residualAll, setField]);

  /* ── Fase 3: API para el modal de 4 pasos ────────────────────────────── */

  /* Aseguradoras reales del plazo actual (del cache), ordenadas por prima.
   * Vacío si el cache no corresponde al auto/precio/placa actuales. Con seguro
   * manual, su única opción: el paso "Seguro" del modal necesita al menos una. */
  const insuranceOptions = quote.manual
    ? [{ id: quote.insuranceId, amount: quote.primaManual, logo: quote.insurerLogo, name: quote.insurer }]
    : (insuranceCache.current.key === cacheKeyNow && state.plazo)
      ? [...(insuranceCache.current.quotes?.[state.plazo] || [])]
        .sort((a, b) => a.amount - b.amount)
        .map(o => ({ ...o, name: insurerName(o.logo) }))
      : [];

  /* Cambia la aseguradora (paso Seguro del modal) → re-cotiza de inmediato.
   * El backend guarda los Insurance del budget con la nueva compañía, requisito
   * para que updateBudget encuentre el internal_id al enviar la solicitud. */
  const setInsurance = useCallback((logo) => {
    // seguro manual: es la única opción; volver a elegirla no recotiza
    if (quote.manual && logo === quote.insurerLogo) return;
    insuranceChoice.current = logo;
    clearTimeout(debounceRef.current);
    runQuote();
  }, [runQuote, quote.manual, quote.insurerLogo]);

  /* ── Seguro manual (P4 · solo admin): API de SeguroManualPanel ────────────
   * editar: guarda lo capturado sin recotizar (vive aquí para no perderse si el
   * panel se desmonta mientras recotiza). aplicar / quitar: recotizan de una,
   * también con la renta ya desbloqueada (igual que "Cotizar ahora"). */
  const editarSeguroManual = useCallback((patch) => {
    setSeguroManual(sm => ({ ...sm, ...(typeof patch === 'function' ? patch(sm) : patch) }));
  }, [setSeguroManual]);

  const aplicarSeguroManual = useCallback(() => {
    setSeguroManual(sm => ({ ...sm, aplicado: true }));
    requote();
  }, [setSeguroManual, requote]);

  const quitarSeguroManual = useCallback(() => {
    setSeguroManual(sm => ({ ...sm, aplicado: false }));
    requote();
  }, [setSeguroManual, requote]);

  /* Guarda nombre + WhatsApp en el Budget apenas el lead llena el formulario,
   * sin esperar a que elija seguro (best-effort: no bloquea el UX si falla).
   *
   * LeadModal ya entrega 10 dígitos limpios (desde el 17-ago solo pide WhatsApp,
   * no correo). El fallback a `email` se queda por si algún otro llamador manda
   * un correo: mejor guardarlo en su campo que meterlo en `phone`. */
  const saveLead = useCallback(async ({ nombre, contacto }) => {
    if (!budgetRef.current.id) return;
    const isEmail = contacto.includes('@');
    try {
      await axios.post(`${url}/saveLead`, {
        budget_id: budgetRef.current.id,
        budget_secret: budgetRef.current.secret,
        name: nombre,
        ...(isEmail ? { email: contacto } : { phone: contacto.replace(/\D/g, '') }),
      });
    } catch (_) { /* best-effort: no interrumpe el flujo */ }
  }, []);

  /* "Continuar por WhatsApp": le pedimos al BACKEND que le mande la plantilla
   * con su cotización, en vez de abrir wa.me y esperar a que el lead teclee y
   * le dé al botón de enviar (WhatsApp nunca autoenvía: eso no se puede).
   *
   * NUNCA lanza. Devuelve {sent, motivo} y el llamador decide: si sent es
   * false, abre wa.me como antes. Un lead sin camino es peor que un lead que
   * tiene que teclear. */
  const enviarWhatsApp = useCallback(async ({ telefono, nombre, auto, anio, plazo, pagoInicial, renta }) => {
    if (!budgetRef.current.id || !telefono) return { sent: false, motivo: 'sin_budget' };
    try {
      const { data } = await axios.post(`${url}/cotizacion/whatsapp`, {
        budget_id: budgetRef.current.id,
        budget_secret: budgetRef.current.secret,
        telefono,
        nombre,
        auto,
        anio,
        plazo,
        pago_inicial: pagoInicial,
        renta,
      });
      return data ?? { sent: false, motivo: 'sin_respuesta' };
    } catch (e) {
      return { sent: false, motivo: e?.response?.data?.motivo || 'error_red' };
    }
  }, []);

  /* ¿Le llegó de verdad? enviarWhatsApp devuelve sent=true cuando META ACEPTÓ,
   * y el fallo real (131049: tope de marketing por usuario) llega por webhook
   * unos segundos después. Sin esta comprobación la landing le dice "listo, te
   * escribimos" a alguien que nunca va a recibir nada.
   *
   * Devuelve 'entregado' | 'fallo' | 'incierto'. El tercero importa: si el
   * webhook se retrasa NO afirmamos nada — mejor un mensaje tibio que decirle
   * "ya puedes chatear con Aria" a quien no recibió nada, o "no te llegó" a
   * quien lo tiene en la mano. */
  const estadoDelWhatsApp = useCallback(async (mensajeId, { intentos = 4, esperaMs = 2500 } = {}) => {
    if (!budgetRef.current.id || !mensajeId) return 'incierto';
    for (let i = 0; i < intentos; i++) {
      await wait(esperaMs);
      try {
        const { data } = await axios.post(`${url}/cotizacion/whatsapp/estado`, {
          budget_id: budgetRef.current.id,
          budget_secret: budgetRef.current.secret,
          mensaje_id: mensajeId,
        });
        if (data?.fallo) return 'fallo';
        if (data?.entregado) return 'entregado';
      } catch (_) { return 'incierto'; }
    }
    return 'incierto';
  }, []);

  /* Envía la solicitud final (paso Solicitud) — espejo de ShowPrice.jsx:184.
   * type=1: "recibir cotización y continuar" (mail con links continue/share). */
  const submitSolicitud = useCallback(async ({ nombre, email, tel }) => {
    if (!budgetRef.current.id || !quote.insuranceId) {
      throw new Error('Tu cotización aún no está lista. Intenta de nuevo.');
    }
    try {
      const res = await axios.post(`${url}/updateBudget`, {
        budget_id: budgetRef.current.id,
        budget_secret: budgetRef.current.secret,
        insurance_id: { id: quote.insuranceId }, // el backend lee insurance_id['id'] (internal_id RODAC)
        name: nombre,
        email,
        phone: tel,
        type: 1,
      });
      return res.data; // {status, share_url, continue_url}
    } catch (err) {
      // el backend responde 500 con un string legible (p.ej. correo ya registrado)
      const data = err?.response?.data;
      const msg = typeof data === 'string' ? data : (data?.error || 'No pudimos enviar tu solicitud. Intenta de nuevo.');
      throw new Error(msg);
    }
  }, [quote.insuranceId]);

  return {
    ...h,
    switchMode,
    restartAI,
    aiReal: aiResolved,
    catalog: { years, brands, models, versions, loadingField },
    real: { onYear, onMarca, onModelo, onVersion },
    quote,
    retryQuote: runQuote,
    budget: budgetRef.current,
    insuranceOptions,
    setInsurance,
    saveLead,
    enviarWhatsApp,
    estadoDelWhatsApp,
    submitSolicitud,
    residualOptions,
    setResidual,
    aiOptions,
    pickVariant,
    staleQuote,
    requote,
    setManualQuote,
    placas,
    downPayment,
    esAdmin,
    seguroManual,
    editarSeguroManual,
    aplicarSeguroManual,
    quitarSeguroManual,
  };
}
